import { Link as RouterLink } from 'react-router-dom';
import { Text, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  container: {
    flex: '1 1 auto',
    padding: '40px 16px',
    maxWidth: '720px',
    margin: '0 auto',
    width: '100%',
    '@media (max-width: 480px)': {
      padding: '24px 12px',
    },
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: tokens.shadow16,
    wordWrap: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    '@media (max-width: 480px)': {
      padding: '24px 16px',
    },
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '24px',
    fontSize: '14px',
    color: tokens.colorBrandBackground,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
  title: {
    display: 'block',
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '24px',
    color: tokens.colorNeutralForeground1,
  },
  updated: {
    display: 'block',
    fontSize: '13px',
    opacity: 0.6,
    marginBottom: '24px',
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    display: 'block',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
    color: tokens.colorNeutralForeground1,
  },
  body: {
    fontSize: '14px',
    lineHeight: '1.7',
    color: tokens.colorNeutralForeground2,
  },
  footer: {
    padding: '20px 16px',
    textAlign: 'center' as const,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '6px',
    fontSize: '12px',
    lineHeight: '1.6',
    opacity: 0.6,
  },
  footerLink: {
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
});

export default function PrivacyPolicy() {
  const styles = useStyles();

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.card}>
          <RouterLink to="/" className={styles.backLink}>← Back to Sign In</RouterLink>
          <Text className={styles.title}>Privacy Policy</Text>
          <Text className={styles.updated}>Last updated: March 2026</Text>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>1. Introduction</Text>
            <p className={styles.body}>
              Component6 Pty Ltd ("we", "us", "our") operates the Sherpai platform. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our
              service. By accessing or using Sherpai, you agree to the collection and use of
              information in accordance with this policy.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>2. Information We Collect</Text>
            <p className={styles.body}>
              We collect information you provide directly, including your name, email address, and
              organisational details when you sign in with your Microsoft account. We also collect
              usage data such as interaction patterns, feature usage, and aggregated analytics to
              improve our service. Conversation data processed by our AI assistant is used solely to
              provide the service and is not used to train AI models.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>3. How We Use Your Information</Text>
            <p className={styles.body}>
              We use collected information to provide and maintain the Sherpai service, authenticate
              your identity, generate aggregated analytics for your organisation, improve platform
              features and user experience, and communicate important service updates. We do not sell
              your personal information to third parties.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>4. Data Storage and Security</Text>
            <p className={styles.body}>
              Your data is stored securely in Microsoft Azure data centres. We implement
              industry-standard security measures including encryption at rest and in transit,
              role-based access controls, and regular security audits. Data retention is governed by
              our configurable retention policies, and you may request deletion of your data at any time.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>5. Your Rights</Text>
            <p className={styles.body}>
              You have the right to access, correct, or delete your personal data. You may withdraw
              consent at any time or request a portable copy of your data. To exercise these rights,
              contact your organisation's IT administrator or reach out to us directly at
              privacy@component6.com.au.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>6. Changes to This Policy</Text>
            <p className={styles.body}>
              We may update this Privacy Policy from time to time. We will notify you of any material
              changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </div>
        </div>
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <RouterLink to="/privacy" className={styles.footerLink}>Privacy Policy</RouterLink>
          <span>·</span>
          <RouterLink to="/terms" className={styles.footerLink}>Terms of Service</RouterLink>
          <span>·</span>
          <RouterLink to="/ai-disclosure" className={styles.footerLink}>AI Disclosure</RouterLink>
          <span>·</span>
          <span>© 2026 Component6 Pty Ltd</span>
        </div>
      </footer>
    </div>
  );
}
